import { useCallback, useEffect, useRef, useState } from "react";
import { Layout } from "react-grid-layout";

import { showToast } from "@/features/shared/components";
import { useTranslation } from "@/lib/i18n";
import { useUpdatePageLayoutMutation } from "@/features/admin/features/pages-management/mutations";
import { transformGridLayoutToWidgetContainers } from "@/features/admin/features/pages-management/utils";
import { isLayoutEqual } from "@/features/admin/features/pages-management/utils/is-layout-equal";
import {
  createLayoutSaveQueueState,
  enqueueLayoutSave,
  isLayoutSavePending,
  LayoutSaveQueueState,
  onSaveSettle,
  onStructuralSync,
} from "@/features/admin/features/pages-management/utils/layout-save-queue";

type Options = {
  pageId: string;
  layout: Layout[];
  /** When false, layout changes are ignored (e.g. page not loaded yet). */
  enabled?: boolean;
  /**
   * True while the user is mid drag or resize. Layout may change freely, but
   * save only starts once this returns to false.
   */
  isInteracting?: boolean;
};

/**
 * Layout auto-save with a single trailing queue while a request is in flight.
 * No timer — in-flight network latency is the debounce. Exposes `isPending` for
 * the beforeunload exit guard.
 *
 * The baseline it compares against only moves forward on a successful save or
 * on an explicit reset, never on a refetch, so saving cannot pull the visible
 * layout back to an intermediate server state.
 */
export function useDebouncedLayoutSave({
  pageId,
  layout,
  enabled = true,
  isInteracting = false,
}: Options) {
  const { t } = useTranslation();
  const { mutate } = useUpdatePageLayoutMutation();

  const queueRef = useRef<LayoutSaveQueueState>(createLayoutSaveQueueState([]));
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  const pageIdRef = useRef(pageId);
  pageIdRef.current = pageId;

  const hasBaselineRef = useRef(false);
  /** Bumped on reset so responses from a previous page cannot settle this one. */
  const sessionRef = useRef(0);
  /**
   * Auto-save only reacts to user interactions. react-grid-layout also emits a
   * normalized layout right after mount, which must not turn opening a page
   * into a write.
   */
  const armedRef = useRef(false);

  const [isPending, setIsPending] = useState(false);

  const syncPending = useCallback(() => {
    const dirty =
      armedRef.current && !isLayoutEqual(layoutRef.current, queueRef.current.lastPersisted);

    setIsPending(isLayoutSavePending(queueRef.current) || dirty);
  }, []);

  const runMutateRef = useRef<(payload: Layout[]) => void>(() => undefined);

  runMutateRef.current = (payload: Layout[]) => {
    const session = sessionRef.current;

    const settle = (outcome: "success" | "error") => {
      if (session !== sessionRef.current) return;

      const result = onSaveSettle(queueRef.current, outcome);
      queueRef.current = result.state;

      if (result.shouldMutate) {
        runMutateRef.current(result.shouldMutate);
        return;
      }

      if (outcome === "success") {
        showToast(t("admin.messages.page_layout_saved"));
      }
      syncPending();
    };

    mutate(
      {
        pageId: pageIdRef.current,
        data: transformGridLayoutToWidgetContainers(payload),
      },
      {
        onSuccess: () => settle("success"),
        onError: () => settle("error"),
      },
    );
  };

  /** Adopt a layout as the persisted baseline, dropping any pending save. */
  const resetPersistedBaseline = useCallback(
    (next: Layout[]) => {
      sessionRef.current += 1;
      queueRef.current = createLayoutSaveQueueState(next);
      hasBaselineRef.current = true;
      armedRef.current = false;
      syncPending();
    },
    [syncPending],
  );

  /** Called when the user starts a drag or resize. */
  const armSave = useCallback(() => {
    armedRef.current = true;
    syncPending();
  }, [syncPending]);

  /** Containers were added or removed; rebase the baseline onto the new ids. */
  const syncPersistedStructure = useCallback((serverLayout: Layout[]) => {
    if (!hasBaselineRef.current) return;
    queueRef.current = onStructuralSync(queueRef.current, serverLayout);
  }, []);

  useEffect(() => {
    if (!enabled || !pageId || !hasBaselineRef.current || !armedRef.current) return;

    if (isLayoutEqual(layout, queueRef.current.lastPersisted)) {
      syncPending();
      return;
    }

    // Hold the save until the pointer is up; intermediate frames must not flush.
    if (isInteracting) {
      syncPending();
      return;
    }

    const result = enqueueLayoutSave(queueRef.current, layout);
    queueRef.current = result.state;
    if (result.shouldMutate) {
      runMutateRef.current(result.shouldMutate);
    }
    syncPending();
  }, [layout, enabled, pageId, isInteracting, syncPending]);

  return { isPending, armSave, resetPersistedBaseline, syncPersistedStructure };
}
